import { Model, ModelAttributes, Query, collection, model, modelConfig } from "@mixitone/oom";
import { isNil } from "@mixitone/util";
import AccountUser from "./AccountUser";
import Club from "./Club";
import Invite from "./Invite";
import { withCachedPromise } from "./concerns/withCachedPromise";
import { Database } from "./database.types";

export const AvailableRegions = {
  au: "Australia",
  nz: "New Zealand",
  uk: "United Kingdom",
  us: "United States",
  eu: "Europe",
  global: "Other",
} as const;

export const Currencies = {
  au: "aud",
  nz: "nzd",
  uk: "gbp",
  us: "usd",
  eu: "eur",
  global: "usd",
};

type AccountFeature =
  | "tournaments"
  | "member_players"
  | "ai_agent"
  | "funding"
  | "group_levels"
  | "akahu_tournament_payments"
  | "akahu_enduring";

const config = modelConfig(
  {
    country: {
      type: "string",
    },
    created_at: {
      type: "datetime",
    },
    is_deleted: {
      type: "boolean",
    },
    is_temporary: {
      type: "boolean",
    },
    plan: {
      type: "enum",
      enumType: "Free" as Database["public"]["Enums"]["account_plan"],
    },
    region: {
      type: "string",
    },
    status: {
      type: "enum",
      enumType: "Trial" as Database["public"]["Enums"]["account_status"],
    },
    timezone: {
      type: "string",
    },
    updated_at: {
      type: "datetime",
    },
    user_id: {
      type: "string",
    },
    welcome_email_sent_at: {
      type: "datetime",
    },
  },
  {
    accountUsers: collection(() => AccountUser, "account_id"),
    invites: collection(() => Invite, "account_id"),
    clubs: collection(() => Club as any, "account_id"),
  },
  {},
);

interface Account extends ModelAttributes<typeof config> {
  get region(): keyof typeof AvailableRegions;
  set region(value: keyof typeof AvailableRegions);
}

@model("accounts", config)
class Account extends Model<typeof config> {
  static _current: Account | undefined;
  static get current() {
    if (isNil(AccountUser.current?.account)) return this._current;
    return AccountUser.current.account;
  }
  static set current(account: Account | null | undefined) {
    if (AccountUser.current && account) {
      throw new Error("Cannot set current account when there is already a current account");
    }
    this._current = account ?? undefined;
  }

  @withCachedPromise(200)
  async creditBalance() {
    if ("invoke_select" in Account.adapter) {
      // @ts-ignore
      const response = await Account.adapter.invoke_select(
        "select sum(balance) as balance from credits where account_id = ? and (expires_at is null or expires_at > ?)",
        [this.id, "today"],
      );
      return response[0].balance ?? 0;
    } else if ("supabase" in Account.adapter) {
      const supabase = Account.adapter.supabase;
      // @ts-ignore
      const response = await supabase.rpc("calculate_unexpired_balance", {
        account_id_param: this.id!,
      });
      return response.data ?? 0;
    }
  }

  get isAnonymous() {
    return this.is_temporary && isNil(this.user_id);
  }
  get isSignedUp() {
    return !this.isAnonymous;
  }

  get isPro(): boolean {
    return this.plan === "Pro";
  }

  async owner(): Promise<AccountUser | undefined> {
    await this.accountUsers.load();
    return this.accountUsers.find((row) => row.user_id === this.user_id);
  }

  features: string[] = [];

  async loadFeatures(): Promise<void> {
    const adapter = Account.adapter;
    // Direct query
    const response = await adapter?.execute<{ feature_name: string }>(
      new Query(
        {
          adapter,
          tableName: "account_features",
          config: { fields: { feature_name: { type: "string" } }, collections: {}, references: {} },
        } as any,
        { adapter },
      )
        .eq("account_id", this.id!)
        .select("feature_name"),
    );
    if (isNil(response?.data)) throw new Error("No response");
    this.features = response.data.map((r) => r.feature_name);
  }

  hasFeature(featureName: AccountFeature): boolean {
    return this.features.includes(featureName);
  }

  get currency(): string {
    const region = this.region as keyof typeof AvailableRegions;
    return Currencies[region] || Currencies.global;
  }
}

export default Account;
